/* eslint-disable */

import axios from 'axios';
import { extractApiError } from '@/utils/apiError';

axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

export default {
  namespaced: true,
  
  state: {
    slackStatus: null,
    channels: [],
    loading: false,
    error: null
  },

  getters: {
    slackStatus: (state) => state.slackStatus,
    channels: (state) => state.channels,
    isConnected: (state) => state.slackStatus && state.slackStatus.connected,
    loading: (state) => state.loading,
    error: (state) => state.error
  },

  mutations: {
    setSlackStatus(state, status) {
      state.slackStatus = status;
    },
    setChannels(state, channels) {
      state.channels = channels;
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setError(state, error) {
      state.error = error;
    },
    clearSlackData(state) {
      state.slackStatus = null;
      state.channels = [];
      state.error = null;
    }
  },

  actions: {
    /**
     * Get the Slack OAuth install URL to start the connection process
     * @param {string} siteID - The site ID
     * @param {string} returnUrl - Optional URL to redirect back to after OAuth (defaults to current origin)
     */
    async getInstallUrl({ commit, dispatch }, { siteID, returnUrl }) {
      try {
        commit('setLoading', true);
        commit('setError', null);
        const response = await axios.get(`/slack/install`, {
          params: { 
            siteID,
            returnUrl: returnUrl || window.location.origin  // Send current origin so callback redirects back here
          },
          crossdomain: true
        });
        commit('setLoading', false);
        return response.data;
      } catch (err) {
        console.error('Error getting Slack install URL:', err);
        commit('setLoading', false);
        commit('setError', extractApiError(err, 'Failed to get install URL'));
        throw err;
      }
    },

    /**
     * Get the current Slack integration status for a site
     */
    async getSlackStatus({ commit, dispatch }, { siteID }) {
      try {
        commit('setLoading', true);
        commit('setError', null);
        const response = await axios.get(`/slack/status`, {
          params: { siteID },
          crossdomain: true
        });
        commit('setSlackStatus', response.data);
        commit('setLoading', false);
        return response.data;
      } catch (err) {
        console.error('Error getting Slack status:', err);
        commit('setLoading', false);
        commit('setError', extractApiError(err, 'Failed to get Slack status'));
        throw err;
      }
    },

    /**
     * Get the list of channels for a connected Slack workspace
     */
    async getChannels({ commit, dispatch }, { siteID }) {
      try {
        commit('setLoading', true);
        commit('setError', null);
        const response = await axios.get(`/slack/channels`, {
          params: { siteID },
          crossdomain: true
        });
        commit('setChannels', response.data.channels || []);
        commit('setLoading', false);
        return response.data;
      } catch (err) {
        console.error('Error getting Slack channels:', err);
        commit('setLoading', false);
        commit('setError', extractApiError(err, 'Failed to get channels'));
        throw err;
      }
    },

    /**
     * Send a message to a Slack channel
     */
    async sendMessage({ commit, dispatch }, { siteID, channelId, message }) {
      try {
        commit('setLoading', true);
        commit('setError', null);
        const response = await axios.post(`/slack/send`, {
          siteID,
          channelID: channelId,  // Backend expects channelID (uppercase ID)
          message
        }, { crossdomain: true });
        commit('setLoading', false);
        return response.data;
      } catch (err) {
        console.error('Error sending Slack message:', err);
        commit('setLoading', false);
        commit('setError', extractApiError(err, 'Failed to send message'));
        throw err;
      }
    },

    /**
     * Disconnect Slack integration for a site
     */
    async disconnect({ commit, dispatch }, { siteID }) {
      try {
        commit('setLoading', true);
        commit('setError', null);
        const response = await axios.post(`/slack/disconnect`, {
          siteID
        }, { crossdomain: true });
        commit('clearSlackData');
        commit('setLoading', false);
        return response.data;
      } catch (err) {
        console.error('Error disconnecting Slack:', err);
        commit('setLoading', false);
        commit('setError', extractApiError(err, 'Failed to disconnect'));
        throw err;
      }
    }
  }
};
